import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  CircularProgress,
  Typography,
  Switch,
  Paper,
} from "@mui/material";
import Iconify from "../iconify/Iconify";
import { fNumber } from "src/utils/formatNumber";
import { fDateTime } from "src/utils/formatTime";

const VersusLineupsTableContent = ({
  lineups,
  filters,
  toggleIgnoredLoading,
  handleSort,
  getSortIcon,
  handleOpenLineupDialog,
  handleOpenCardsDialog,
  handleSwitchChange,
  loadingMore,
}) => {
  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} aria-label="versus lineups table">
        <TableHead>
          <TableRow>
            <TableCell
              onClick={() => handleSort("versusGameLineupId")}
              sx={{
                cursor: "pointer",
                userSelect: "none",
                fontWeight:
                  filters.orderBy === "versusGameLineupId" ? "bold" : "normal",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              ID{getSortIcon("versusGameLineupId")}
            </TableCell>
            <TableCell
              onClick={() => handleSort("username")}
              sx={{
                cursor: "pointer",
                userSelect: "none",
                fontWeight: filters.orderBy === "username" ? "bold" : "normal",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              Username{getSortIcon("username")}
            </TableCell>
            <TableCell
              onClick={() => handleSort("score")}
              sx={{
                cursor: "pointer",
                userSelect: "none",
                fontWeight: filters.orderBy === "score" ? "bold" : "normal",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              Score{getSortIcon("score")}
            </TableCell>
            <TableCell
              onClick={() => handleSort("fpts")}
              sx={{
                cursor: "pointer",
                userSelect: "none",
                fontWeight: filters.orderBy === "fpts" ? "bold" : "normal",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              Fpts{getSortIcon("fpts")}
            </TableCell>
            <TableCell
              sx={{
                cursor: "pointer",
                userSelect: "none",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
              title="Click to toggle between active/ignored status"
            >
              Active Status
            </TableCell>
            <TableCell
              onClick={() => handleSort("createdAt")}
              sx={{
                cursor: "pointer",
                userSelect: "none",
                fontWeight: filters.orderBy === "createdAt" ? "bold" : "normal",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              Created At{getSortIcon("createdAt")}
            </TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lineups.length === 0 && (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography
                  variant="body2"
                  sx={{ padding: 2, textAlign: "center", width: "100%" }}
                >
                  No lineups found
                </Typography>
              </TableCell>
            </TableRow>
          )}

          {lineups.map((row, index) => {
            return (
              <TableRow
                key={index}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                }}
              >
                <TableCell component="th" scope="row">
                  <Button
                    variant="text"
                    color="primary"
                    onClick={() =>
                      handleOpenLineupDialog(row.versusGameLineupId)
                    }
                    sx={{
                      minWidth: "auto",
                      p: 0.5,
                      fontSize: "inherit",
                      fontWeight: "normal",
                      textTransform: "none",
                    }}
                  >
                    {row.versusGameLineupId}
                  </Button>
                </TableCell>
                <TableCell>
                  <a href={`/dashboard/profile?user_id=${row.userId}`}>
                    {row.username}
                  </a>
                </TableCell>
                <TableCell>{fNumber(row.score || 0)}</TableCell>
                <TableCell>{fNumber(row.fpts || 0)}</TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    padding: "16px 12px",
                  }}
                >
                  {toggleIgnoredLoading === row.versusGameLineupId ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "48px",
                        gap: 1,
                      }}
                    >
                      <CircularProgress size={20} thickness={4} />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Switch
                        checked={!row.isIgnored}
                        onChange={(event) =>
                          handleSwitchChange(
                            event,
                            row.versusGameLineupId,
                            row.isIgnored
                          )
                        }
                        disabled={
                          toggleIgnoredLoading === row.versusGameLineupId
                        }
                        size="medium"
                      />
                    </Box>
                  )}
                </TableCell>
                <TableCell>{fDateTime(row.createdAt)}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        handleOpenLineupDialog(row.versusGameLineupId)
                      }
                      startIcon={<Iconify icon="eva:eye-outline" />}
                      sx={{
                        minWidth: "auto",
                        px: 1,
                        py: 0.5,
                      }}
                    >
                      Lineup
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        handleOpenCardsDialog(row.userId, row.username)
                      }
                      startIcon={<Iconify icon="eva:grid-outline" />}
                      sx={{
                        minWidth: "auto",
                        px: 1,
                        py: 0.5,
                      }}
                    >
                      Cards
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {loadingMore && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
            backgroundColor: "#f9f9f9",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 2, color: "text.secondary" }}>
            Loading more lineups...
          </Typography>
        </Box>
      )}
    </TableContainer>
  );
};

export default VersusLineupsTableContent;
